import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { useMergedRefs } from "../internal/refs.js";
import { useControllableState } from "./hooks.js";
import { CloseIcon } from "./icons.js";
import { useAppShell, SidebarPlacementProvider } from "./ShellContext.js";
import { DialogRoot } from "../vendor/@base-ui/react/dialog/root/DialogRoot.js";
import { DialogPortal } from "../vendor/@base-ui/react/dialog/portal/DialogPortal.js";
import { DialogBackdrop } from "../vendor/@base-ui/react/dialog/backdrop/DialogBackdrop.js";
import { DialogPopup } from "../vendor/@base-ui/react/dialog/popup/DialogPopup.js";
import { DialogClose } from "../vendor/@base-ui/react/dialog/close/DialogClose.js";
const Drawer = React.forwardRef(function Drawer2({
  children,
  open: openProp,
  defaultOpen,
  onOpenChange,
  label = "Navigation",
  closeLabel = "Close navigation",
  app,
  finalFocus,
  className,
  onClick,
  ...rest
}, ref) {
  const shell = useAppShell();
  const fromShell = openProp === void 0 && defaultOpen === void 0 && shell != null;
  const [ownOpen, setOwnOpen] = useControllableState(openProp, defaultOpen ?? false, onOpenChange);
  const open = fromShell ? shell.drawerOpen : ownOpen;
  const setOpen = React.useCallback(
    (next) => {
      if (fromShell) {
        shell.setDrawerOpen(next);
        onOpenChange?.(next);
      } else {
        setOwnOpen(next);
      }
    },
    [fromShell, shell, onOpenChange, setOwnOpen]
  );
  const popupRef = React.useRef(null);
  const setRefs = useMergedRefs(ref, popupRef);
  const handleClick = (event) => {
    onClick?.(event);
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target.closest("a[href], [data-ev-drawer-close]") : null;
    if (target && popupRef.current?.contains(target)) setOpen(false);
  };
  const initialFocus = React.useCallback((type) => {
    if (type === "touch") return true;
    const current = popupRef.current?.querySelector('[aria-current="page"]');
    return current ?? true;
  }, []);
  const returnFocus = React.useCallback(() => finalFocus?.() ?? true, [finalFocus]);
  return /* @__PURE__ */ jsx(DialogRoot, { open, onOpenChange: (next) => setOpen(next), children: /* @__PURE__ */ jsxs(DialogPortal, { children: [
    /* @__PURE__ */ jsx(DialogBackdrop, { className: "ev-drawer-scrim" }),
    /* @__PURE__ */ jsxs(
      DialogPopup,
      {
        ref: setRefs,
        className: cx("ev-drawer", "ev-root", className),
        "aria-label": label,
        "data-app": app ?? shell?.app,
        initialFocus,
        finalFocus: returnFocus,
        onClick: handleClick,
        ...rest,
        children: [
          /* @__PURE__ */ jsx(DialogClose, { className: "ev-btn ev-btn--ghost ev-btn--icon ev-drawer__close", "aria-label": closeLabel, children: /* @__PURE__ */ jsx(CloseIcon, {}) }),
          /* @__PURE__ */ jsx(SidebarPlacementProvider, { value: "drawer", children })
        ]
      }
    )
  ] }) });
});
export {
  Drawer
};
//# sourceMappingURL=Drawer.js.map
